import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, Inject } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { filter, Subscription, take, takeUntil } from 'rxjs';
import { ShareService } from '@core/services/share-data/share-data.service';
import { SELECTORS, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { SVGData } from '@root/app.interfaces';
import { VariantService } from '@core/services/variant/variant.service';
import { GtmService } from '@core/services/gtm/gtm.service2';
import { QUIZ_MODAL_EVENTS } from '@core/services/gtm/gtm.events2';

@Component({
	selector: 'app-quiz-modal',
	templateUrl: './quiz-modal.component.html',
	styleUrls: ['./quiz-modal.component.scss', './quiz-modal.responsive.scss'],
})
export class QuizModalComponent implements OnInit, AfterViewInit, OnDestroy {
	static readonly ID = crypto.randomUUID();
	protected readonly IS_SHOP = this.variant.isShop();

	getViewChangeHandler: Subscription;

	isMobile: boolean;
	isDesignType: boolean;
	isQuizReviewed: boolean;
	selectOpeningMode: boolean;
	firstSlideViewed = true;
	highestSlideIndex = 0;
	closedByEscape = false;

	slideConfig = {
		slidesToShow: 1,
		slidesToScroll: 1,
		dots: true,
		infinite: false,
		swipe: false,
		arrows: false,
		autoplay: false,
		adaptiveHeight: true,
		customPaging: () => {
			return '<div class="slick-dot-custom"></div>';
		},
	};

	quizIconsFolder = '/assets/icons/quiz/';
	slidesQuantity: number;
	currentSlideIndex: number;
	svgData: SVGData[] = [];
	IVQuizNames = ['move', 'resize', 'rotate', 'display', 'toggle_iv', 'send'];
	DYBQuizNames = ['size', 'toggle_dyb', 'send'];
	DetectionQuizNames = ['auto_detect', 'manual_add', 'multiple_blinds'];
	MobileQuizNames = ['manual_add'];

	get quizTypeKey(): 'dyb' | 'detection' | 'iv' {
		return this.isDesignType ? 'dyb' : this.selectOpeningMode ? 'detection' : 'iv';
	}

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: { context: null | 'detection_error' },
		private variant: VariantService,
		protected sanitizer: DomSanitizer,
		private shareDataService: ShareService,
		private setupPageService: SetupPageService,
		private changeDetection: ChangeDetectorRef,
		private sessionStorageService: SessionStorageService,
		private matDialogRef: MatDialogRef<QuizModalComponent>,
		private gtm: GtmService,
	) {}

	ngOnInit(): void {
		if (this.data?.context === 'detection_error') {
			this.slideConfig['initialSlide'] = 1;
			this.firstSlideViewed = false;
		}
		this.isMobile = this.setupPageService.mobileAndTabletCheck();
		this.selectOpeningMode = this.shareDataService.getSelectOpeningModeValue();
		this.quizCloseHandler();
		this.quizStatusHandler();
		this.getViewTypeFormStorage();
		this.changeStyleForOverlay();
		this.getSVG();
	}

	ngAfterViewInit(): void {
		this.changeDetection.detectChanges();
	}

	ngOnDestroy(): void {
		this.getViewChangeHandler.unsubscribe();
	}

	closeDialog(closeMethod: string = null, reviewed: boolean = false): void {
		this.matDialogRef.close({ closeMethod, reviewed });
	}

	carouselHandler(event: any): void {
		const slick = event.slick;
		const slideTrack = slick.$slideTrack;
		const children = slideTrack[0].children;

		let isColumnMode = true;

		for (const child of children) {
			if (window.getComputedStyle(child).flexDirection !== 'column') {
				isColumnMode = false;
				break;
			}
		}

		if (event.event?.type === 'init' && isColumnMode) {
			const slider = slick.$slider;
			const firstImg = slider.find(SELECTORS.quiz_icon);
			firstImg.on('load', () => {
				const slide = slick.$slides[0];
				const img = slide.querySelector(SELECTORS.quiz_icon);
				const height = img.offsetHeight;
				const firstChildHeight = slide.children[0].offsetHeight;
				slick.$list.height(firstChildHeight + height);
			});
		}

		this.slidesQuantity = slick.slideCount;
		this.currentSlideIndex = slick.currentSlide;
		this.isQuizReviewed = this.currentSlideIndex === this.slidesQuantity - 1 || this.isQuizReviewed;
		this.highestSlideIndex = this.currentSlideIndex > this.highestSlideIndex ? this.currentSlideIndex : this.highestSlideIndex;
		if (this.currentSlideIndex === 0) {
			this.firstSlideViewed = true;
		}
	}

	onLoadImage(event: Event): void {
		let isColumnMode = true;
		const slideTrack = document.querySelector(SELECTORS.slick_track) as HTMLElement;

		if (!slideTrack) {
			return;
		}

		const children = slideTrack.children;

		for (const child of children) {
			if (window.getComputedStyle(child).flexDirection !== 'column') {
				isColumnMode = false;
				break;
			}
		}

		if (!isColumnMode) {
			return;
		}

		const img = event.target as HTMLElement;
		const height = img.offsetHeight;
		const firstChildHeight = (img.parentElement.firstElementChild as HTMLElement).offsetHeight;

		const slick = document.querySelector(SELECTORS.slick_list) as HTMLElement;
		slick.style.height = `${firstChildHeight + height}px`;
	}

	quizCloseHandler(): void {
		this.getViewChangeHandler = this.shareDataService.getViewType.subscribe((type: string) => {
			this.isDesignType = type === VIEW_TYPES.design;

			if (this.isDesignType) {
				this.closeDialog('view_change');
			} else {
				const quizStatuses = this.sessionStorageService.getSession(STORAGE_NAMES.zip_quiz_status);

				quizStatuses.iv = true;

				this.sessionStorageService.setSession(quizStatuses, STORAGE_NAMES.zip_quiz_status);
			}

			this.getSVG();
		});
	}

	quizStatusHandler(): void {
		this.matDialogRef
			.keydownEvents()
			.pipe(
				filter((event) => event.key === 'Escape' || event.key === 'Esc'),
				take(1),
				takeUntil(this.matDialogRef.afterClosed()),
			)
			.subscribe(() => {
				this.closedByEscape = true;
			});

		this.matDialogRef.afterOpened().subscribe(this.switchQuizStatus.bind(this, true));
		this.matDialogRef.afterClosed().subscribe((res) => {
			this.switchQuizStatus(false, res?.reviewed);
			this.updateReviewedQuizStatus(!!res?.reviewed);
			this.highestSlideIndex = this.firstSlideViewed ? this.highestSlideIndex + 1 : this.highestSlideIndex;
			const closeMethod = res?.closeMethod ? res.closeMethod : this.closedByEscape ? 'escape' : 'unknown';
			this.gtm.pushEvent(QUIZ_MODAL_EVENTS.close, {
				close_method: closeMethod,
				quiz_type: this.quizTypeKey,
				slides_viewed: this.highestSlideIndex,
				viewed_to_end: !!this.isQuizReviewed,
			});
		});
	}

	switchQuizStatus(status: boolean, reviewed: boolean = false): void {
		this.shareDataService.setQuizStatus({
			status,
			reviewed,
		});
		this.setUntouchableOverlay(status);
	}

	updateReviewedQuizStatus(status: boolean) {
		const quizReviewedStatus = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_quiz_reviewed_status);
		const currentQuizReviewedStatus = {
			...quizReviewedStatus,
			[this.quizTypeKey]: status,
		};

		this.sessionStorageService.setSession(currentQuizReviewedStatus, STORAGE_NAMES.zip_quiz_reviewed_status);
	}

	setUntouchableOverlay(status: boolean): void {
		const overlay = document.querySelector(SELECTORS.overlay_container);

		overlay.classList.toggle('untouchable', status);
	}

	getViewTypeFormStorage(): void {
		const type = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type);
		const quizReviewedStatus = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_quiz_reviewed_status);

		this.isDesignType = type === VIEW_TYPES.design;
		this.isQuizReviewed = quizReviewedStatus?.[this.quizTypeKey];
	}

	changeStyleForOverlay(): void {
		const overlay = this.matDialogRef['_ref']?.['overlayRef'];
		const overlayElement = overlay.overlayElement as HTMLElement;
		const hasBackdrop = overlay['_config'].hasBackdrop;

		overlayElement.parentElement.style.zIndex = hasBackdrop ? '1000' : '999';
	}

	getSVG(): void {
		const svgNames = this.isDesignType ? this.DYBQuizNames : this.selectOpeningMode ? this.DetectionQuizNames : this.IVQuizNames;
		this.svgData = [];

		svgNames.forEach((name) => {
			const fileName = this.isMobile && this.MobileQuizNames.includes(name) ? `${name}_mobile` : name;
			const file = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.quizIconsFolder}${fileName}.svg`);

			this.svgData.push({ name, file });
		});
	}
}
